import * as Shared from "./MemberManager.shared";
import { SupabaseClient, User } from "@supabase/supabase-js";
import { MembershipRow, TenantRow, InvitationRow } from "@/lib/types";
import { DatabaseManager } from "@/lib/database/DatabaseManager.client";
import { IMemberManager } from "./IMemberManager";

export class MemberManager implements IMemberManager {
  private client: SupabaseClient;

  constructor() {
    this.client = DatabaseManager.createBrowserClient();
  }

  getTenantInfo(
    tenantId: string
  ): Promise<Pick<TenantRow, "id" | "name" | "slug"> | null> {
    return Shared.getTenantInfo(this.client, tenantId);
  }

  getTenantInfoBySlug(
    slug: string
  ): Promise<Pick<TenantRow, "id" | "name" | "slug"> | null> {
    return Shared.getTenantInfoBySlug(this.client, slug);
  }

  getAllTenants(): Promise<Pick<TenantRow, "id" | "name" | "slug">[]> {
    return Shared.getAllTenants(this.client);
  }

  findUserByEmail(email: string): Promise<string | null> {
    return Shared.findUserByEmail(this.client, email);
  }

  checkUserMembership(userId: string, tenantId: string): Promise<boolean> {
    return Shared.checkUserMembership(this.client, userId, tenantId);
  }

  getUserMembershipRole(
    userId: string,
    tenantId: string
  ): Promise<"admin" | "member" | null> {
    return Shared.getUserMembershipRole(this.client, userId, tenantId);
  }

  getUserMembershipType(
    userId: string,
    tenantId: string
  ): Promise<
    | "owner"
    | "board-member"
    | "renter"
    | "property-manager"
    | "portal-admin"
    | null
  > {
    return Shared.getUserMembershipType(this.client, userId, tenantId);
  }

  checkUserIsAdmin(userId: string, tenantId: string): Promise<boolean> {
    return Shared.checkUserIsAdmin(this.client, userId, tenantId);
  }

  async addUserToMembership(
    userId: string,
    tenantId: string,
    memberType: "owner" | "board-member" | "renter" | "property-manager",
    hasAdminPrivileges: boolean
  ): Promise<boolean> {
    return Shared.addUserToMembership(
      this.client,
      userId,
      tenantId,
      memberType,
      hasAdminPrivileges
    );
  }

  createUser(email: string): Promise<string | null> {
    return Shared.createUser(this.client, email);
  }

  inviteUser(
    email: string,
    tenantName: string,
    redirectTo: string
  ): Promise<User> {
    return Shared.inviteUser(this.client, email, tenantName, redirectTo);
  }

  async createInvitationRecord(
    tenantId: string,
    userId: string,
    email: string,
    memberType: "owner" | "board-member" | "renter" | "property-manager",
    hasAdminPrivileges: boolean,
    invitedBy: string,
    message?: string
  ): Promise<boolean> {
    return Shared.createInvitationRecord(
      this.client,
      tenantId,
      userId,
      email,
      memberType,
      hasAdminPrivileges,
      invitedBy,
      message
    );
  }

  getInvitationById(invitationId: string): Promise<InvitationRow | null> {
    return Shared.getInvitationById(this.client, invitationId);
  }

  deleteInvitationById(invitationId: string): Promise<boolean> {
    // Client-side stub - this should not be called on the client
    throw new Error("deleteInvitationById is not available on the client side");
  }

  getMembershipsByTenantId(tenantId: string): Promise<MembershipRow[]> {
    return Shared.getMembershipsByTenantId(this.client, tenantId);
  }

  getMembershipsWithUsersByTenantId(tenantId: string): Promise<any[]> {
    return Shared.getMembershipsWithUsersByTenantId(this.client, tenantId);
  }

  getMembershipsByUserId(userId: string): Promise<MembershipRow[]> {
    return Shared.getMembershipsByUserId(this.client, userId);
  }

  getAllUsers(): Promise<User[]> {
    return Shared.getAllUsers(this.client);
  }

  getUsersByIds(userIds: string[]): Promise<Map<string, User>> {
    return Shared.getUsersByIds(this.client, userIds);
  }

  getPendingInvitationsByTenantId(tenantId: string): Promise<InvitationRow[]> {
    return Shared.getPendingInvitationsByTenantId(this.client, tenantId);
  }

  checkInvitationByEmail(
    email: string,
    tenantId: string
  ): Promise<InvitationRow | null> {
    return Shared.checkInvitationByEmail(this.client, email, tenantId);
  }

  resendInvitation(
    email: string,
    tenantId: string,
    invitationId: string,
    role: string,
    host?: string
  ): Promise<boolean> {
    return Shared.resendInvitation(
      this.client,
      email,
      tenantId,
      invitationId,
      role,
      host
    );
  }

  updateInvitationTimestamp(invitationId: string): Promise<boolean> {
    return Shared.updateInvitationTimestamp(this.client, invitationId);
  }

  updateMembershipRole(
    membershipId: string,
    role: "admin" | "member"
  ): Promise<boolean> {
    return Shared.updateMembershipRole(this.client, membershipId, role);
  }

  updateMembershipMemberType(
    membershipId: string,
    memberType:
      | "owner"
      | "board-member"
      | "renter"
      | "property-manager"
      | "portal-admin"
  ): Promise<boolean> {
    return Shared.updateMembershipMemberType(
      this.client,
      membershipId,
      memberType
    );
  }

  updateMembershipAdminPrivileges(
    membershipId: string,
    hasAdminPrivileges: boolean
  ): Promise<boolean> {
    return Shared.updateMembershipAdminPrivileges(
      this.client,
      membershipId,
      hasAdminPrivileges
    );
  }

  deleteMembershipById(membershipId: string): Promise<boolean> {
    return Shared.deleteMembershipById(this.client, membershipId);
  }

  getMembershipById(membershipId: string): Promise<MembershipRow | null> {
    return Shared.getMembershipById(this.client, membershipId);
  }

  deleteMembershipsByUserId(userId: string): Promise<boolean> {
    return Shared.deleteMembershipsByUserId(this.client, userId);
  }

  deleteInvitationsByUserId(userId: string): Promise<boolean> {
    return Shared.deleteInvitationsByUserId(this.client, userId);
  }

  markInvitationCompletedById(invitationId: string): Promise<boolean> {
    return Shared.markInvitationCompletedById(this.client, invitationId);
  }

  markInvitationsCompletedByUserId(
    userId: string,
    email?: string
  ): Promise<boolean> {
    return Shared.markInvitationsCompletedByUserId(this.client, userId, email);
  }

  cancelInvitationById(invitationId: string): Promise<boolean> {
    return Shared.cancelInvitationById(this.client, invitationId);
  }

  getMembershipByEmail(
    email: string,
    tenantId: string
  ): Promise<MembershipRow | null> {
    return Shared.getMembershipByEmail(this.client, email, tenantId);
  }
}
